/**
 * Algolia Autocomplete Panel — Olay Custom Layout + Add to Cart
 *
 * Responsibilities:
 *  1. Classify Algolia `.aa-Source` elements with CSS classes so that
 *     CSS Grid can arrange them into a 2-column layout WITHOUT touching
 *     innerHTML or moving DOM nodes (which breaks Algolia's Preact renderer).
 *       LEFT  — Popular Searches (pills) + Categories & Collections
 *       RIGHT — Olay Favorites product cards
 *  2. Inject an "Add to Cart" overlay button onto each product image.
 *  3. Handle Add to Cart via Shopify's /cart/add.js AJAX endpoint.
 *
 * No external dependencies. Safe to defer.
 */
(function () {
  'use strict';

  /* ─── Price / variant cache ────────────────────────────────────── */
  var variantCache = {};

  function formatPrice(cents) {
    if (!cents && cents !== 0) return '';
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Fetch the first available variant for a product URL.
   * Caches by handle so repeated opens don't re-fetch.
   */
  function getFirstVariant(productUrl, cb) {
    var m = productUrl && productUrl.match(/\/products\/([^?#/]+)/);
    if (!m) { cb(null); return; }
    var handle = m[1];
    if (variantCache[handle] !== undefined) { cb(variantCache[handle]); return; }

    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Product fetch failed');
        return r.json();
      })
      .then(function (data) {
        var variants = data.variants || [];
        var v = variants.find(function (v) { return v.available; }) || variants[0];
        var result = v ? { id: v.id, price: v.price, tags: (data.tags || []) } : null;
        variantCache[handle] = result;
        cb(result);
      })
      .catch(function () {
        variantCache[handle] = null;
        cb(null);
      });
  }

  /* ─── Add to Cart ──────────────────────────────────────────────── */
  function addToCart(variantId, btn) {
    btn.classList.add('olay-aa-atc--adding');
    btn.querySelector('.olay-aa-atc-text').textContent = 'Adding\u2026';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Failed'); });
        return r.json();
      })
      .then(function () {
        btn.classList.remove('olay-aa-atc--adding');
        btn.classList.add('olay-aa-atc--added');
        btn.querySelector('.olay-aa-atc-text').textContent = 'Added!';

        /* Notify theme cart components to refresh count */
        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));

        setTimeout(function () {
          btn.classList.remove('olay-aa-atc--added');
          btn.querySelector('.olay-aa-atc-text').textContent = 'Add to Cart';
        }, 2000);
      })
      .catch(function () {
        btn.classList.remove('olay-aa-atc--adding');
        btn.classList.add('olay-aa-atc--error');
        btn.querySelector('.olay-aa-atc-text').textContent = 'Error \u2014 Retry';
        setTimeout(function () {
          btn.classList.remove('olay-aa-atc--error');
          btn.querySelector('.olay-aa-atc-text').textContent = 'Add to Cart';
        }, 3000);
      });
  }

  /* ─── Inject ATC button + New badge into a single product item ── */
  function injectProductEnhancements(item) {
    if (item.dataset.olayAaDone) return;
    item.dataset.olayAaDone = '1';

    var icon = item.querySelector('.aa-ItemIcon');
    var link = item.querySelector('.aa-ItemLink, a[href]');
    if (!icon || !link) return;

    /* Extract any already-rendered price text from descriptions */
    var renderedPrice = '';
    item.querySelectorAll('.aa-ItemContentDescription, .aa-ItemContentSubtitle').forEach(function (el) {
      if (!renderedPrice) {
        var m = el.textContent.match(/\$[\d,]+(?:\.\d{2})?/);
        if (m) renderedPrice = m[0];
      }
    });

    /* Build ATC overlay button */
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'olay-aa-atc-overlay';
    btn.setAttribute('aria-label', 'Add to Cart');
    btn.innerHTML =
      '<span class="olay-aa-atc-text">Add to Cart</span>' +
      '<span class="olay-aa-atc-price">' + renderedPrice + '</span>';

    /* Fetch variant data to fill price + wire up button */
    var href = link.getAttribute('href') || '';
    getFirstVariant(href, function (variant) {
      if (!variant) {
        /* No available variant — hide button gracefully */
        btn.style.display = 'none';
        return;
      }
      btn.setAttribute('data-variant-id', String(variant.id));
      if (!renderedPrice && variant.price) {
        btn.querySelector('.olay-aa-atc-price').textContent = formatPrice(variant.price);
      }
      /* Show "New" badge if product is tagged "new" */
      var isNew = variant.tags && variant.tags.some(function (t) {
        return /^\s*new\s*$/i.test(t);
      });
      if (isNew && !icon.querySelector('.olay-aa-new-badge')) {
        var badge = document.createElement('span');
        badge.className = 'olay-aa-new-badge';
        badge.textContent = 'New';
        badge.setAttribute('aria-hidden', 'true');
        icon.appendChild(badge);
      }
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var vid = btn.getAttribute('data-variant-id');
      if (vid) addToCart(vid, btn);
    });

    icon.appendChild(btn);
  }

  /* ─── Identify whether a source contains product cards ─────────── */
  function isProductSource(source) {
    return !!source.querySelector('.aa-ItemIcon img, .aa-ItemIcon--picture');
  }

  /**
   * Classify sources with CSS classes so CSS Grid handles the 2-column
   * layout. We NEVER move or clear DOM nodes — that destroys Algolia's
   * Preact virtual DOM and causes the blank-panel bug.
   */
  function classifySources(panel) {
    var layout = panel.querySelector('.aa-PanelLayout');
    if (!layout) return;

    /* Mark layout so CSS Grid kicks in */
    layout.classList.add('olay-aa-layout-applied');

    var sources = Array.from(layout.querySelectorAll(':scope > .aa-Source'));
    if (!sources.length) return;

    var productSrc = null;
    var leftSources = [];

    sources.forEach(function (src) {
      /* Clear previous classifications for a clean re-run */
      src.classList.remove('olay-aa-pills', 'olay-aa-categories', 'olay-aa-products-source');
      if (isProductSource(src)) {
        productSrc = productSrc || src;
      } else {
        leftSources.push(src);
      }
    });

    /* Fallback: treat last source as products if none have images yet */
    if (!productSrc && sources.length) {
      productSrc = sources[sources.length - 1];
      leftSources = sources.slice(0, -1);
    }

    if (leftSources[0]) leftSources[0].classList.add('olay-aa-pills');
    if (leftSources[1]) leftSources[1].classList.add('olay-aa-categories');
    if (productSrc) {
      productSrc.classList.add('olay-aa-products-source');
      /* Inject ATC buttons on newly rendered items */
      productSrc.querySelectorAll('.aa-Item').forEach(injectProductEnhancements);
    }
  }

  /* ─── Add tabindex to panel content elements ──────────────────────
   *
   * Sets tabindex="0" on .aa-ItemContentBody, .aa-ItemContentTitle,
   * and .aa-SourceHeaderTitle so they are keyboard-reachable.
   * Called every time the panel re-renders.
   * ─────────────────────────────────────────────────────────────── */
  var PANEL_TAB_SELECTORS = [
    '.aa-ItemContentBody',
    '.aa-ItemContentTitle',
    '.aa-SourceHeaderTitle'
  ];

  function addPanelTabIndices(panel) {
    PANEL_TAB_SELECTORS.forEach(function (sel) {
      panel.querySelectorAll(sel).forEach(function (el) {
        el.setAttribute('tabindex', '0');
      });
    });
  }

  /* ─── No-results message ────────────────────────────────────────── */
  var NO_RESULTS_ID = 'olay-aa-no-results-msg';

  /**
   * Show/hide a "No results" message inside the Algolia panel when
   * the user has typed a query but no items were rendered.
   * Works for both inline (desktop) and detached (mobile) modes.
   */
  function updateNoResultsMessage() {
    var panel = document.querySelector('.aa-Panel');
    var existing = document.getElementById(NO_RESULTS_ID);

    /* Get the current query from whichever input is visible */
    var input = document.querySelector('.aa-Input');
    var query = (input && input.value) ? input.value.trim() : '';

    if (!panel || !query) {
      if (existing) existing.remove();
      return;
    }

    var hasItems = !!panel.querySelector('.aa-Item');

    if (!hasItems) {
      if (!existing) {
        var scrollable = panel.querySelector('.aa-Panel--scrollable') || panel;
        var msg = document.createElement('div');
        msg.id = NO_RESULTS_ID;
        msg.className = 'olay-aa-no-results';
        msg.setAttribute('role', 'status');
        msg.setAttribute('aria-live', 'polite');
        msg.textContent = 'No results for \u201c' + query + '\u201d';
        scrollable.appendChild(msg);
      } else {
        /* Update query text in case it changed */
        existing.textContent = 'No results for \u201c' + query + '\u201d';
      }
    } else {
      if (existing) existing.remove();
    }
  }

  /* ─── MutationObserver to catch panel renders ───────────────────── */
  var lastRun = 0;
  var mo = new MutationObserver(function () {
    var now = Date.now();
    if (now - lastRun < 80) return;
    lastRun = now;
    requestAnimationFrame(function () {
      var panel = document.querySelector('.aa-Panel');
      if (panel && panel.querySelector('.aa-Source')) {
        classifySources(panel);
        addPanelTabIndices(panel);
      }
      updateNoResultsMessage();
    });
  });

  /* ─── Tab-order focus management ───────────────────────────────────
   *
   * Goal:  Input → Suggestions → Products → Cancel button
   *
   * Problem: Algolia appends .aa-Panel to document.body AFTER the
   * drawer's .drawer--focus-trigger elements (which close the drawer
   * when focused). So natural DOM tab order never reaches the panel.
   *
   * Solution: Intercept Tab / Shift+Tab in the capture phase (before
   * Algolia's own input-level keydown handler) and redirect focus.
   * ─────────────────────────────────────────────────────────────── */
  function getFocusableItems() {
    /* All visible, keyboard-reachable elements in the open panel,
     * in DOM order: .aa-ItemLink (Algolia anchors) plus the content
     * elements stamped with tabindex="0" by addPanelTabIndices. */
    var panel = document.querySelector('.aa-Panel');
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(
      '.aa-ItemLink, .aa-SourceHeaderTitle, .aa-ItemContentBody, .aa-ItemContentTitle'
    )).filter(function (el) {
      /* Visible + has an explicit non-negative tabindex */
      var ti = el.getAttribute('tabindex');
      return el.offsetParent !== null && ti !== null && ti !== '-1';
    });
  }

  function getSearchInput() {
    var drawer = document.querySelector('.drawer--side[data-side="top"][aria-expanded="true"]');
    if (!drawer) return null;
    return drawer.querySelector('.aa-Input') || document.getElementById('search_input');
  }

  function getCancelButton() {
    var drawer = document.querySelector('.drawer--side[data-side="top"][aria-expanded="true"]');
    return drawer ? drawer.querySelector('.search--cancel') : null;
  }

  function setupFocusOrder() {
    /* Use capture phase so this fires before Algolia's bubble-phase
     * keydown listener on the input, letting us call preventDefault
     * before Algolia closes the panel on Tab. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;

      /* Only act when the search drawer is open */
      var drawer = document.querySelector('.drawer--side[data-side="top"][aria-expanded="true"]');
      if (!drawer) return;

      var active     = document.activeElement;
      var input      = getSearchInput();
      var cancelBtn  = getCancelButton();
      var items      = getFocusableItems();
      var first      = items[0]  || null;
      var last       = items[items.length - 1] || null;

      if (!e.shiftKey) {
        /* Tab forward: Input → first suggestion */
        if (active === input && first) {
          e.preventDefault();
          /* Ensure item is keyboard-reachable */
          if (first.getAttribute('tabindex') === '-1') first.removeAttribute('tabindex');
          first.focus();
          return;
        }
        /* Tab forward: last item → Cancel */
        if (last && active === last && cancelBtn) {
          e.preventDefault();
          cancelBtn.focus();
          return;
        }
      } else {
        /* Shift+Tab: first item → Input */
        if (first && active === first && input) {
          e.preventDefault();
          input.focus();
          return;
        }
        /* Shift+Tab: Cancel → last item */
        if (cancelBtn && active === cancelBtn && last) {
          e.preventDefault();
          last.focus();
          return;
        }
      }
    }, true /* capture phase */);
  }

  /* ─── Focus the Algolia input when the search drawer opens ──────── */
  /*                                                                     */
  /* The theme's Drawer.opened() already calls                          */
  /* document.getElementById('search_input').focus()                    */
  /* but Algolia wraps or replaces that element with .aa-Input,         */
  /* so the native focus doesn't reach the visible Algolia input.       */
  /*                                                                     */
  /* We watch for aria-expanded="true" on the top drawer via            */
  /* MutationObserver (same one used for panel classifying) and then    */
  /* focus .aa-Input after the CSS transition finishes (~400ms theme).  */
  function focusSearchInput() {
    /* Detached mode: Algolia renders .aa-DetachedSearchButton which    */
    /* it programmatically clicks — focus is handled by Algolia itself. */
    /* Non-detached mode: focus .aa-Input inside the drawer.            */
    var aaInput = document.querySelector(
      '.drawer--side[data-side="top"] .aa-Input'
    );
    if (aaInput) {
      aaInput.focus();
      return;
    }
    /* Fallback: native input */
    var nativeInput = document.getElementById('search_input');
    if (nativeInput) nativeInput.focus();
  }

  var topDrawer = null;
  var focusTimer = null;

  function watchTopDrawer() {
    topDrawer = document.querySelector('.drawer--side[data-side="top"]');
    if (!topDrawer) return;

    var drawerObs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (
          m.type === 'attributes' &&
          m.attributeName === 'aria-expanded' &&
          topDrawer.getAttribute('aria-expanded') === 'true'
        ) {
          clearTimeout(focusTimer);
          /* Wait for the drawer's CSS transition to finish before focusing */
          focusTimer = setTimeout(focusSearchInput, 420);
        }
      });
    });

    drawerObs.observe(topDrawer, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  /* ─── Mobile: left-align SUGGESTIONS header once panel is visible ── */
  function applyMobileSuggestionHeaderMargin() {
    if (window.innerWidth > 767) return;

    var panel = document.querySelector('.aa-Panel');
    if (!panel) return;

    var headerObs = new MutationObserver(function () {
      var header = panel.querySelector('.aa-SourceHeader:first-child');
      if (header) {
        header.style.marginLeft = '5%';
      }
    });

    headerObs.observe(panel, { childList: true, subtree: true });

    /* Apply immediately in case sources are already rendered */
    var header = panel.querySelector('.aa-SourceHeader:first-child');
    if (header) header.style.marginLeft = '5%';
  }

  function watchAlgoliaPanelForMobile() {
    if (window.innerWidth > 767) return;

    var panelObs = new MutationObserver(function () {
      var panel = document.querySelector('.aa-Panel');
      if (panel) {
        applyMobileSuggestionHeaderMargin();
        panelObs.disconnect();
      }
    });

    panelObs.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    mo.observe(document.body, { childList: true, subtree: true });
    watchTopDrawer();
    setupFocusOrder();
    watchAlgoliaPanelForMobile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
